import * as React from "react";
import { Modal, notification } from "antd";
import { request, resourceLink } from "../../../../services/cpln";
import { useCpFormModifiedAnyId } from "../../../gvc/testNewForm/utils";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";

interface UseNodePoolScriptsProps {
  clusterName: string;
  nodePoolName: string;
}

interface UseNodePoolScriptsResult {
  joinScript: string;
  cloudinitScript: string;
  handleJoinScript: () => Promise<void>;
  handleCloudinitScript: () => Promise<void>;
  clearJoinScript: () => void;
  clearCloudinitScript: () => void;
}

export function useNodePoolScripts({ clusterName, nodePoolName }: UseNodePoolScriptsProps): UseNodePoolScriptsResult {
  const { id: formId } = useCpFormContext();
  const isDirty = useCpFormModifiedAnyId(formId);

  const [joinScript, setJoinScript] = React.useState<string>("");
  const [cloudinitScript, setCloudinitScript] = React.useState<string>("");

  async function handleJoinScript() {
    if (isDirty) {
      Modal.warning({
        title: "Node Pool Join Script",
        content: "Please save your changes before trying to create the join script.",
      });
      return;
    }

    const notificationKey: string = "joinScript";
    notification.open({
      key: notificationKey,
      type: "info",
      message: "Join Script",
      description: "Fetching data from the server, please wait",
    });

    try {
      const {
        data: { script },
      } = await request({
        method: "post",
        url: resourceLink("mk8s", clusterName) + "/-join",
        body: {
          nodePoolName: nodePoolName,
        },
      });
      setJoinScript(script);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to create join script", description: errorMessage });
    }
    notification.destroy(notificationKey);
  }

  async function handleCloudinitScript() {
    if (isDirty) {
      Modal.warning({
        title: "Node Pool Cloud Init Script",
        content: "Please save your changes before trying to create the cloud init script.",
      });
      return;
    }

    const notificationKey: string = "cloudinitScript";
    notification.open({
      key: notificationKey,
      type: "info",
      message: "Cloud Init Script",
      description: "Fetching data from the server, please wait",
    });

    try {
      const {
        data: { script },
      } = await request({
        method: "post",
        url: resourceLink("mk8s", clusterName) + "/-cloudinit",
        body: {
          nodePoolName: nodePoolName,
        },
      });
      setCloudinitScript(script);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to create cloud init script", description: errorMessage });
    }
    notification.destroy(notificationKey);
  }

  function clearJoinScript() {
    setJoinScript("");
  }

  function clearCloudinitScript() {
    setCloudinitScript("");
  }

  return {
    joinScript,
    cloudinitScript,
    handleJoinScript,
    handleCloudinitScript,
    clearJoinScript,
    clearCloudinitScript,
  };
}
